import { cn } from "~/lib/ui";

import { type TelehealthValueProp, telehealthValueProps } from "./constants";

/**
 * ET-216 — the four-item trust row under the telehealth PDP's Get Started CTA,
 * restyled by ET-509 to XD-149 node 3298-17209: upper-case 18px medium in the
 * site's brand colour (`text-minicart-cost` — FSA #2458A9 / HSA #55449A / WDH
 * #8D3888, the same token the cart name and price use). Icons inherit the
 * colour via `currentColor`.
 *
 * Two content-sized columns only from `xl` (1240px in this config, not stock
 * 1280). At 18px upper-case the pair needs ~540px (more with WDH's "FSA/HSA"
 * label), and the info column is narrower than that until ~1180px — measured
 * 480px at 768–1024 — so an `md` breakpoint pushed "No Membership/Consult Fee"
 * out of the column.
 */
export function TelehealthValueProps({
  siteLabel,
  items,
  className,
}: {
  /**
   * "FSA" / "HSA" / "FSA/HSA", or `null` on a site with no eligibility label.
   * Required — no default, so this can't quietly ship FSA copy to HSA/WDH.
   */
  siteLabel: string | null;
  items?: readonly TelehealthValueProp[];
  className?: string;
}) {
  const props = items ?? telehealthValueProps(siteLabel);

  if (!props.length) return null;

  return (
    <ul
      className={cn(
        // Arbitrary rem gaps: this repo's spacing scale is non-stock, so
        // `gap-x-4` is not 16px. Figma: 16px between columns, 10px between rows.
        "grid grid-cols-1 gap-x-[1rem] gap-y-[0.625rem] font-grotesque text-lg font-medium uppercase leading-[1.25rem] text-minicart-cost xl:grid-cols-[repeat(2,max-content)]",
        className,
      )}
      data-testid="pdp-value-props-section"
    >
      {props.map(({ Icon, label }) => (
        <li key={label} className="flex items-center gap-[0.25rem]">
          {/* Explicit 24px: this repo's Tailwind scale is non-stock, where
              `h-6` resolves to 32px. */}
          <Icon className="h-[1.5rem] w-[1.5rem] shrink-0" />
          <span>{label}</span>
        </li>
      ))}
    </ul>
  );
}
